import moment from 'moment';

import { FieldValidator } from '@/modules/dynamic-form/models/FieldValidator.model';
import { useDynamicFormStore } from '@/modules/dynamic-form/store';

import { interpretMessage } from '../../utils/interpret-message/interpretMessage';
import { CustomMessageOptions } from '../type';

const MESSAGE = 'Must be after {{afterField}}';

/**
 * Creates a reactive validator that checks if a date is after another date.
 * The 'after' parameter can be either a static string or a getter function
 * that returns the latest value from the store, making it reactive to form changes.
 * this is DEPENDENT VALIDATION which means the input control needs to watch for dependent field value changes
 * and trigger validate when it happens
 *
 * @param after - A date string or a getter function that returns a date string
 * @param msgOptions - Options for custom messages and field names
 * @returns A validator function that returns true if valid, or an error message string if invalid
 *
 * @example
 * // Static value (not reactive)
 * createAfterValidator('2025-01-01', { fieldName: 'End Date' })
 *
 * @example
 * // Reactive value using a getter function
 * createAfterValidator(
 *   () => useDynamicFormStore().forms[formId].values['policy.effectiveDate'],
 *   { fieldName: 'Expiration Date', afterField: 'Effective Date' }
 * )
 */
export function createAfterValidator(
    after: string | (() => string),
    msgOptions?: CustomMessageOptions & { afterField?: string },
): FieldValidator {
    const validate = (val: string) => {
        const momentVal = moment(val);
        // Resolve the 'after' value - if it's a function, call it to get the current value
        const afterValue = typeof after === 'function' ? after() : after;
        const momentAfter = moment(afterValue);

        if (!val || val === '' || !momentAfter.isValid()) {
            // if there is no value we should consider it valid as it should be part of required validator
            // if the after value is invalid, there is no constraint to check hence should be valid
            return true;
        }
        if (!momentVal.isValid()) {
            console.warn('value used in after validator is invalid', val);
            return 'Invalid date';
        }
        if (momentVal.isAfter(momentAfter)) {
            return true;
        }

        const message = msgOptions?.customMsg || MESSAGE;
        return interpretMessage(
            message,
            {
                field: msgOptions?.fieldName || 'field',
                afterField: msgOptions?.afterField || momentAfter.format('M/D/YYYY') || afterValue,
            },
        );
    };

    return { name: 'date.after', validate };
}
